import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import MangaCard from "@/components/base/MangaCard";
import { categories, mangaList, heroCover } from "@/mocks/manga";

const featured = [...mangaList].sort((a, b) => b.rating - a.rating).slice(0, 6);
const hotRank = [...mangaList].sort((a, b) => b.rating - a.rating).slice(0, 5);

const features = [
  { icon: "ri-filter-3-line", title: "按甜度筛选", desc: "从微甜到爆甜，甜度条一目了然，帮你精准找到对味的那一部。" },
  { icon: "ri-coupon-3-line", title: "标签鉴定", desc: "甜宠、纯爱、少女、狗粮…多维标签体系，搜索不迷路，找漫画不踩雷。" },
  { icon: "ri-notification-3-line", title: "追更提醒", desc: "订阅喜欢的作品，更新第一时间通知你，再也不怕错过新章节。" },
  { icon: "ri-bookmark-3-line", title: "收藏记录", desc: "章节排序、阅读进度、收藏清单全记录，随时接着上次读到的地方看。" },
];

const faqs = [
  { q: "布丁漫画网收录哪些类型的漫画？", a: "布丁漫画网专注甜系恋爱漫画，主要收录甜宠漫画、恋爱漫画、高甜漫画、少女漫画、纯爱漫画和狗粮漫画，覆盖校园、都市、和风、奇幻等多种题材。" },
  { q: "如何快速找到自己想看的漫画？", a: "你可以在漫画库中按甜度、画风、题材和分类进行筛选，也可以直接搜索作品名、标签或作者名，收藏记录和追更提醒会帮你整理想追的作品。" },
  { q: "甜度评分是怎么来的？", a: "甜度评分由内容编辑结合发糖密度、情感浓度和剧情走向综合评定，从一颗心到五颗心，方便你按自己的喜好挑选合适甜度的作品。" },
  { q: "追更提醒和收藏记录如何使用？", a: "登录后点击漫画页面的收藏按钮即可加入书架，作品更新时会收到提醒；你的阅读进度会自动保存，下次进入直接跳到上次阅读的位置。" },
  { q: "可以按 CP 和画风筛选吗？", a: "可以。漫画库提供画风筛选，标签中也包含 CP 类型、题材与氛围等维度，帮助你更快找到符合喜好的甜系恋爱漫画。" },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl bg-background-50 ring-1 ring-background-200">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-foreground-900">{q}</span>
        <i className={`ri-add-line text-lg text-primary-500 transition-transform ${open ? "rotate-45" : ""}`} aria-hidden="true"></i>
      </button>
      {open && <p className="px-5 pb-4 text-sm leading-6 text-foreground-600">{a}</p>}
    </div>
  );
}

function SectionHead({ title, sub, to }: { title: string; sub?: string; to?: string }) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <h2 className="font-heading text-2xl text-foreground-900 md:text-3xl">{title}</h2>
        {sub && <p className="mt-1 text-sm text-foreground-500">{sub}</p>}
      </div>
      {to && (
        <Link to={to} className="whitespace-nowrap text-sm text-primary-600 hover:text-primary-700">
          查看全部 <i className="ri-arrow-right-line ml-0.5" aria-hidden="true"></i>
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(keyword.trim() ? `/list?q=${encodeURIComponent(keyword.trim())}` : "/list");
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "布丁漫画网",
        url: "https://stripvault.github.io/",
        description: "专注甜系恋爱漫画阅读的在线漫画网站。",
      },
      {
        "@type": "WebSite",
        name: "布丁漫画网",
        url: "https://stripvault.github.io/",
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: "https://stripvault.github.io/list?q={search_term_string}" },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <Header />
      <main>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-background-100 via-background-50 to-primary-100/60 pt-16">
          <div className="pointer-events-none absolute -right-16 -top-10 h-64 w-64 rounded-full bg-accent-200/50 blur-3xl" aria-hidden="true"></div>
          <div className="pointer-events-none absolute -left-10 bottom-0 h-56 w-56 rounded-full bg-secondary-200/60 blur-3xl" aria-hidden="true"></div>
          <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-heart-3-fill text-accent-500" aria-hidden="true"></i>
                今天也要甜甜的
              </span>
              <h1 className="mt-4 font-heading text-3xl leading-tight text-foreground-950 md:text-5xl">
                布丁漫画网
                <span className="mt-1 block text-primary-500">只为甜甜的恋爱存在</span>
              </h1>
              <p className="mt-4 max-w-xl text-base leading-7 text-foreground-600">
                收录甜宠、恋爱、高甜、少女、纯爱、狗粮六大甜系分类漫画。按甜度、CP、画风一键筛选，追更提醒、章节排序、收藏记录都备好，找高甜狗粮直接进。
              </p>
              <form onSubmit={submitSearch} className="mt-6 flex max-w-md flex-col gap-3 sm:flex-row sm:items-center" role="search">
                <div className="relative flex-1">
                  <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" aria-hidden="true"></i>
                  <input
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    type="text"
                    placeholder="搜漫画 / 标签 / 作者"
                    className="h-12 w-full rounded-full border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                    aria-label="搜索漫画"
                  />
                </div>
                <button type="submit" className="h-12 whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                  开始找漫画
                </button>
              </form>
              <div className="mt-7 flex flex-wrap gap-6">
                {[
                  { num: "1200+", label: "甜系漫画" },
                  { num: "6", label: "甜系分类" },
                  { num: "98%", label: "读者好评" },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="font-heading text-2xl text-primary-600">{s.num}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] ring-1 ring-primary-200">
                <img src={heroCover} alt="布丁漫画网甜系恋爱漫画阅读场景" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -left-3 top-6 rounded-2xl bg-background-50/90 px-4 py-3 shadow-[0_4px_20px_oklch(var(--primary-300)/0.35)] backdrop-blur sm:-left-6">
                <p className="text-xs text-foreground-500">甜度爆表</p>
                <p className="mt-0.5 flex items-center gap-1 text-sm font-medium text-foreground-900">
                  <i className="ri-heart-fill text-accent-500" aria-hidden="true"></i>4.8 / 5.0
                </p>
              </div>
              <div className="absolute -bottom-4 right-4 rounded-2xl bg-background-50/90 px-4 py-3 shadow-[0_4px_20px_oklch(var(--primary-300)/0.35)] backdrop-blur">
                <p className="text-xs text-foreground-500">今日更新</p>
                <p className="mt-0.5 text-sm font-medium text-primary-600">《樱花色的告白》</p>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16" aria-labelledby="cat-heading">
          <SectionHead id="cat-heading" title="甜系分类" sub="六大分类，按你的口味挑" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {categories.map((c) => (
              <Link
                key={c.name}
                to={`/list?cat=${encodeURIComponent(c.name)}`}
                className="group flex flex-col items-center rounded-2xl bg-background-50 px-4 py-6 text-center ring-1 ring-background-200 transition-all hover:-translate-y-1 hover:ring-primary-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100 text-2xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={c.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-3 text-sm font-medium text-foreground-900">{c.name}漫画</h3>
                <p className="mt-1 text-xs text-foreground-500">{c.desc}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured */}
        <section className="bg-background-100/70 py-12 md:py-16" aria-labelledby="featured-heading">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHead id="featured-heading" title="读者最爱" sub="高分甜系，闭眼入不踩雷" to="/list?tab=hot" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {featured.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot rank + latest */}
        <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHead title="高甜热榜" sub="本周最受欢迎的甜系" to="/list?tab=hot" />
              <ol className="space-y-3">
                {hotRank.map((m, idx) => (
                  <li key={m.id}>
                    <Link
                      to={`/manga/${m.id}`}
                      className="flex items-center gap-4 rounded-2xl bg-background-50 p-3 ring-1 ring-background-200 transition-all hover:ring-primary-300"
                    >
                      <span className={`w-6 text-center font-heading text-lg ${idx < 3 ? "text-primary-500" : "text-foreground-400"}`}>
                        {idx + 1}
                      </span>
                      <img src={m.cover} alt={`${m.title}封面`} loading="lazy" className="h-16 w-12 rounded-lg object-cover object-top" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground-900">{m.title}</p>
                        <p className="mt-1 text-xs text-foreground-500">
                          {m.category} · <i className="ri-heart-fill text-accent-500" aria-hidden="true"></i> {m.sweetness.toFixed(1)}
                        </p>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-secondary-700">
                        <i className="ri-star-fill text-secondary-500" aria-hidden="true"></i>
                        {m.rating.toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
            <div className="lg:col-span-8">
              <SectionHead title="最新更新" sub="追更不停，甜度不停" to="/list" />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {mangaList.slice(0, 8).map((m) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-gradient-to-br from-primary-50 via-background-50 to-accent-50/60 py-12 md:py-16" aria-labelledby="feature-heading">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHead id="feature-heading" title="为什么选布丁" sub="为甜系读者打造的阅读体验" />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((f) => (
                <div key={f.title} className="rounded-2xl bg-background-50 p-6 ring-1 ring-background-200">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-100 text-2xl text-accent-600">
                    <i className={f.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 text-base font-medium text-foreground-900">{f.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto w-full max-w-4xl px-4 py-12 md:px-6 md:py-16" aria-labelledby="faq-heading">
          <SectionHead id="faq-heading" title="常见问题" sub="关于布丁漫画网，你想知道的都在这里" />
          <div className="space-y-3">
            {faqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="relative overflow-hidden rounded-[2rem] bg-primary-500 px-6 py-12 text-center md:px-10 md:py-16">
            <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-background-50/15 blur-2xl" aria-hidden="true"></div>
            <div className="pointer-events-none absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-background-50/15 blur-2xl" aria-hidden="true"></div>
            <h2 className="relative font-heading text-2xl text-background-50 md:text-4xl">今天，想看点甜的吗？</h2>
            <p className="relative mx-auto mt-3 max-w-xl text-sm leading-6 text-background-50/90 md:text-base">
              打开漫画库，按甜度挑一部，钻进被窝享受甜甜的恋爱吧。
            </p>
            <Link
              to="/list"
              className="relative mt-6 inline-flex whitespace-nowrap rounded-full bg-background-50 px-7 py-3 text-sm font-medium text-primary-600 transition-transform hover:-translate-y-0.5"
            >
              进入漫画库 <i className="ri-arrow-right-line ml-1" aria-hidden="true"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}